import { AppUpdate, AppUpdateAvailability, AppUpdateResultCode } from '@capawesome/capacitor-app-update';
import { i18n } from 'boot/i18n';
import { CapacitorAndroidStoreProvider } from 'capacitor-android-store-provider';
import { compare } from 'compare-versions';
import { Dialog, Platform } from 'quasar';
import { notifyInfo } from 'src/composables/interactions';
import NativeAppVersionsDB from 'src/fb/nativeAppVersions-db';
import { nativeAppUpdates as useNativeAppUpdatesStore } from 'src/store/nativeAppUpdates';
import { logEvent } from 'src/utils/LogEvent';

const APP_STORES = {
  APPLE_APP_STORE: 'apple-app-store',
  AMAZON_APP_STORE: 'amazon-app-store',
  GOOGLE_PLAY_STORE: 'google-play-store',
  UNKNOWN: 'unknown',
};

const UPDATE_TYPES = {
  APP_STORE: 'app-store',
  IMMEDIATE: 'immediate',
};

const APP_UPDATE_PROPOSITION_INTERVAL = 1000 * 60 * 60 * 24; // 1 day

const LAST_APP_UPDATE_PROPOSITION_TIME_KEY = 'lastNativeAppUpdatePropositionTime';

// eslint-disable-next-line import/prefer-default-export
export function useNativeAppUpdates() {
  const nativeAppUpdates = useNativeAppUpdatesStore();

  const isNativeApp = Platform.is.capacitor && (Platform.is.ios || Platform.is.android);

  async function getAppStore() {
    if (!isNativeApp) {
      throw new Error('This function is only available on iOS and Android native apps');
    }

    if (Platform.is.ios) {
      return APP_STORES.APPLE_APP_STORE;
    }

    try {
      const { installerPackageName } = await CapacitorAndroidStoreProvider.getInstallerPackageName();

      switch (installerPackageName) {
        case 'com.amazon.venezia': {
          return APP_STORES.AMAZON_APP_STORE;
        }
        case 'com.android.vending': {
          return APP_STORES.GOOGLE_PLAY_STORE;
        }
        default: {
          return APP_STORES.UNKNOWN;
        }
      }
    } catch (err) {
      console.error(err);
      logEvent(err);
      return APP_STORES.UNKNOWN;
    }
  }

  async function getAppUpdateInfo(appStore) {
    try {
      switch (appStore) {
        case APP_STORES.APPLE_APP_STORE: {
          const { updateAvailability } = await AppUpdate.getAppUpdateInfo();

          if (updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
            return { updateAvailable: false };
          }

          return { updateAvailable: true, type: UPDATE_TYPES.APP_STORE };
        }
        case APP_STORES.AMAZON_APP_STORE: {
          const nativeAppVersionsDb = new NativeAppVersionsDB();

          const latestAmazonAppData = await nativeAppVersionsDb.read('amazon');

          if (!latestAmazonAppData?.version) {
            return { updateAvailable: false };
          }

          if (compare(latestAmazonAppData.version, process.env.version, '>')) {
            return { updateAvailable: true, type: UPDATE_TYPES.APP_STORE };
          }

          return { updateAvailable: false };
        }
        case APP_STORES.GOOGLE_PLAY_STORE: {
          const { updateAvailability, immediateUpdateAllowed } = await AppUpdate.getAppUpdateInfo();

          if (updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
            return { updateAvailable: false };
          }

          return {
            updateAvailable: true,
            type: immediateUpdateAllowed ? UPDATE_TYPES.IMMEDIATE : UPDATE_TYPES.APP_STORE,
          };
        }
        default: {
          return { updateAvailable: false };
        }
      }
    } catch (err) {
      console.error(err);
      logEvent(err);
      return { updateAvailable: false };
    }
  }

  async function updateApp(appStore, updateType) {
    try {
      switch (appStore) {
        case APP_STORES.APPLE_APP_STORE: {
          // App ID is hardcoded for the iOS app, because we don't have a way to get it dynamically.
          // ID can be found in the App Store URL.
          await AppUpdate.openAppStore({ appId: '1544967962' });
          break;
        }
        case APP_STORES.AMAZON_APP_STORE: {
          await CapacitorAndroidStoreProvider.openAmazonAppStore();
          break;
        }
        case APP_STORES.GOOGLE_PLAY_STORE: {
          if (updateType === UPDATE_TYPES.IMMEDIATE) {
            const { code } = await AppUpdate.performImmediateUpdate();

            switch (code) {
              case AppUpdateResultCode.FAILED: {
                await AppUpdate.openAppStore();
                break;
              }
              case AppUpdateResultCode.INFO_MISSING: {
                // If user cancels the immediate update, we can't show the immediate update again, because library
                // clears its internal update info after the first time user cancels the immediate update.
                // So, we need to reinitialize the internal update info to show the immediate update again.
                await AppUpdate.getAppUpdateInfo();
                await AppUpdate.performImmediateUpdate();
                break;
              }
              default: {
                break;
              }
            }
          } else {
            await AppUpdate.openAppStore();
          }
          break;
        }
        default: {
          break;
        }
      }
    } catch (err) {
      console.error(err);
      logEvent(err);
    }
  }

  function showAppUpdateConfirmationDialog(callback) {
    sessionStorage.setItem(LAST_APP_UPDATE_PROPOSITION_TIME_KEY, Date.now().toString());

    return Dialog.create({
      title: i18n.t('nativeAppVersionUpdate.title'),
      message: i18n.t('nativeAppVersionUpdate.text'),
      persistent: true,
      ok: {
        label: i18n.t('nativeAppVersionUpdate.ok'),
        flat: true,
      },
      cancel: {
        label: i18n.t('common.buttonLater'),
        color: 'secondary',
        flat: true,
      },
    })
      .onOk(callback);
  }

  function showAppUpdateBanner(callback) {
    sessionStorage.setItem(LAST_APP_UPDATE_PROPOSITION_TIME_KEY, Date.now().toString());

    return notifyInfo(`${i18n.t('nativeAppVersionUpdate.title')} ${i18n.t('nativeAppVersionUpdate.text')}`, {
      icon: 'cached',
      classes: 'notify-update-version',
      progress: false,
      timeout: 0,
      actions: [{
        label: i18n.t('common.buttonLater'),
      }, {
        label: i18n.t('nativeAppVersionUpdate.ok'),
        handler: callback,
      }],
    });
  }

  function shouldShowAppUpdateBanner() {
    const isInOfflineMode = JSON.parse(localStorage.getItem('offlineModeStatus'));

    if (
      !isNativeApp
      || isInOfflineMode
      || !nativeAppUpdates.hasNewNativeAppVersion
    ) {
      return false;
    }

    const lastAppUpdatePropositionTime = sessionStorage.getItem(LAST_APP_UPDATE_PROPOSITION_TIME_KEY);

    if (!lastAppUpdatePropositionTime) {
      return false;
    }

    return Date.now() - parseInt(lastAppUpdatePropositionTime, 10) >= APP_UPDATE_PROPOSITION_INTERVAL;
  }

  async function initializeNativeAppUpdateFlow() {
    if (!isNativeApp) {
      return;
    }

    const isInOfflineMode = JSON.parse(localStorage.getItem('offlineModeStatus'));

    if (isInOfflineMode) {
      return;
    }

    try {
      const appStore = await getAppStore();
      const appUpdateInfo = await getAppUpdateInfo(appStore);
      await nativeAppUpdates.setAppUpdateInfo({ appStore, ...appUpdateInfo });

      if (appUpdateInfo.updateAvailable) {
        showAppUpdateConfirmationDialog(() => updateApp(appStore, appUpdateInfo.type));
      }
    } catch (err) {
      console.error(err);
      logEvent(err);
    }
  }

  return {
    updateApp,
    showAppUpdateBanner,
    shouldShowAppUpdateBanner,
    initializeNativeAppUpdateFlow,
  };
}
